import { ConsentStatus, IEConsentForm } from '@/api/dtos';
import EConsentContext, { EConsentUserType } from '@/contexts/EConsentContext';
import { faCheckCircle, faTimesCircle } from '@fortawesome/pro-duotone-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Alert, AlertTitle, Box, Paper, styled, Typography } from '@mui/material';
import { FormContext, FormDefinitionContext, IFormContext } from '@ngt/opms';
import { FC, PropsWithChildren, useContext } from 'react';

const PageWrapper = styled(Box)(({ theme }) => ({
    padding: 0,
    [theme.breakpoints.up('md')]: {
        maxWidth: 1000,
        margin: 'auto',
        padding: theme.spacing(3),
    },
}));

const PagePaper = styled(Paper)(({ theme }) => ({
    padding: theme.spacing(3),
    [theme.breakpoints.down('xs')]: {
        padding: theme.spacing(5, 2),
        border: 'none',
        boxShadow: 'none',
    },
}));

const PageHeader = styled(Typography)(({ theme }) => ({
    paddingBottom: theme.spacing(1.5),
    textAlign: 'center',
    fontSize: '1.25rem',
    color: theme.palette.primary.main,
    [theme.breakpoints.down('sm')]: {
        textAlign: 'left',
        width: '80%',
    },
}));

interface StatePanelProps {
    severity: 'error' | 'success';
    title: string;
    children: string;
}

const StatePanel: FC<StatePanelProps> = ({ severity, title, children }) => (
    <PageWrapper>
        <PagePaper elevation={3}>
            <Alert
                severity={severity}
                icon={<FontAwesomeIcon fixedWidth icon={severity === 'success' ? faCheckCircle : faTimesCircle} />}
            >
                <AlertTitle>{title}</AlertTitle>
                {children}
            </Alert>
        </PagePaper>
    </PageWrapper>
);

const EConsentPage: FC<PropsWithChildren> = ({ children }) => {
    const { userType, consentStatus } = useContext(EConsentContext);
    const { data: form } = useContext(FormContext) as unknown as IFormContext<IEConsentForm>;
    const { data: formDefinition } = useContext(FormDefinitionContext);
    const formName = formDefinition?.name ?? 'Consent';
    const lowerFormName = formName.toLowerCase();

    if (!form || !userType || userType === EConsentUserType.Unknown) {
        return (
            <StatePanel severity="error" title={`${formName} Not Loaded`}>
                {`An error occurred while loading the ${lowerFormName}. Please report the issue to your treatment team.`}
            </StatePanel>
        );
    }

    if (userType === EConsentUserType.Investigator && !form.patientSignedConsent) {
        return (
            <StatePanel severity="error" title={`${formName} Not Available`}>
                {`The ${lowerFormName} is still awaiting the participant's signature.`}
            </StatePanel>
        );
    }

    if (userType === EConsentUserType.Participant && consentStatus === ConsentStatus.AwaitingInvestigatorSignature) {
        return (
            <StatePanel severity="success" title={`${formName} Submitted`}>
                {`The ${lowerFormName} has been submitted successfully.`}
            </StatePanel>
        );
    }

    if (consentStatus === ConsentStatus.Completed) {
        return (
            <StatePanel severity="success" title={`${formName} Completed`}>
                {`The ${lowerFormName} has been completed successfully.`}
            </StatePanel>
        );
    }

    return (
        <PageWrapper>
            <PagePaper elevation={3}>
                <PageHeader><strong>{formName.toUpperCase()}</strong></PageHeader>
                {children}
            </PagePaper>
        </PageWrapper>
    );
};

export default EConsentPage;
